import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Box, Button, Dialog, DialogContent, DialogTitle, Grid2 as Grid } from '@mui/material';
import { EVENT_ROLE, STATUS, User } from '@type/db-types';
import FormInputText from '@components/forms/FormInputText';
import FormInputSelect from '@components/forms/FormInputSelect';
import { EVENT_ROLE_OPTIONS } from '@utils/event-utils';
import { inviteUserToEvent } from '@modules/api';
import useEventContext from '@hooks/useEventContext';
import invariant from 'tiny-invariant';

interface Props {
    onClose: () => void;
    open: boolean;
}

interface AddAmin extends Pick<User, 'firstName' | 'lastName'> {
    role: EVENT_ROLE;
    email: string;
}
const defaultValues: Partial<AddAmin> = {
    firstName: '',
    lastName: '',
    email: '',
    role: EVENT_ROLE.STAFF,
};

export default function AddAdminModal({ onClose, open }: Props) {
    const { event } = useEventContext();
    const formMethods = useForm<AddAmin>({
        defaultValues,
        mode: 'all',
        resolver: yupResolver(addAdminSchema),
    });

    const { handleSubmit, formState, reset } = formMethods;
    const { errors } = formState;
    const disabled = Object.keys(errors).length > 0;

    const onSubmit: SubmitHandler<AddAmin> = async (values) => {
        console.log(`Inviting user`, { values });
        invariant(event, 'Event must be defined');

        await inviteUserToEvent({
            ...values,
            eventId: event?.id,
            membershipStatus: STATUS.INVITED,
        }).then((success) => {
            if (success) {
                reset();
                onClose();
            }
        });
    };

    return (
        <FormProvider {...formMethods}>
            <form>
                <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
                    <DialogTitle sx={{ marginTop: '1rem', marginLeft: '1rem' }}>
                        Add Event Admin
                    </DialogTitle>
                    <DialogContent>
                        <Grid
                            container
                            spacing={4}
                            padding={4}
                            paddingTop={2}
                            alignItems={'flex-end'}
                        >
                            <Grid size={{ sm: 12, md: 6 }} alignSelf={'center'}>
                                <FormInputText name="firstName" label="First Name" />
                            </Grid>
                            <Grid size={{ sm: 12, md: 6 }} alignSelf={'center'}>
                                <FormInputText name="lastName" label="Last Name" />
                            </Grid>
                            <Grid size={{ sm: 12, md: 6 }} alignSelf={'center'}>
                                <FormInputText name="email" label="Email" />
                            </Grid>
                            <Grid size={{ sm: 12, md: 6 }} alignSelf={'center'}>
                                <FormInputSelect
                                    label="Event Role"
                                    name="role"
                                    options={EVENT_ROLE_OPTIONS}
                                />
                            </Grid>
                            <Box
                                display="flex"
                                flexDirection="row"
                                width="100%"
                                justifyContent="space-between"
                            >
                                <Button variant="outlined" color="error" onClick={onClose}>
                                    Cancel
                                </Button>
                                <Button
                                    disabled={disabled}
                                    variant="contained"
                                    onClick={handleSubmit(onSubmit)}
                                >
                                    Invite
                                </Button>
                            </Box>
                        </Grid>
                    </DialogContent>
                </Dialog>
            </form>
        </FormProvider>
    );
}

const addAdminSchema: Yup.ObjectSchema<AddAmin> = Yup.object({
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    email: Yup.string().required(),
    role: Yup.mixed<EVENT_ROLE>().required(),
});
